<template>
  <div class="admin">
    <h1>Facilitator hub</h1>
    <p class="muted">
      Manage colleges, approve students, and open the deck. Pick a college below to view or
      clear its votes. Approved users can access the slides at any time.
    </p>
    <div class="actions">
      <router-link class="btn btn-primary" to="/admin/colleges">Manage colleges</router-link>
      <router-link class="btn btn-secondary" to="/admin/users">Manage users</router-link>
      <router-link class="btn btn-secondary" to="/select-college">View a college</router-link>
      <router-link class="btn btn-secondary" to="/slides/01">Open slide deck</router-link>
      <router-link class="btn btn-secondary" to="/admin/guides">Guides</router-link>
    </div>

    <section class="card polls">
      <h2>Polls</h2>
      <p class="muted">
        Clear vote numbers for the college you are viewing. This does not assign you to
        that college. You can also clear one slide at a time from Results on the deck.
      </p>
      <div class="scope">
        <label>
          College
          <select v-model="collegeId" :disabled="busy">
            <option disabled value="">Select college</option>
            <option v-for="c in colleges" :key="c.id" :value="c.id">{{ collegeLabel(c, colleges) }}</option>
          </select>
        </label>
      </div>
      <p v-if="error" class="error">{{ error }}</p>
      <p v-else-if="status" class="status">{{ status }}</p>
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="busy || !canClear"
        @click="clearAll"
      >
        {{ busy ? 'Clearing…' : 'Clear all votes for this college' }}
      </button>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { slides } from '../content/deck'
import { collegeLabel } from '../services/workshopAccess'
import { getAdminSession, setAdminSession } from '../services/adminSession'
import { adminListColleges, clearPollResults } from '../services/api'

const colleges = ref([])
const collegeId = ref('')
const selected = computed(() => colleges.value.find(c => c.id === collegeId.value))
const canClear = computed(() => !!selected.value)
const busy = ref(false)
const error = ref('')
const status = ref('')
function scope() { return { collegeId: selected.value.id, collegeName: selected.value.name } }
watch(collegeId, () => {
  status.value = ''
  if (selected.value) setAdminSession(scope())
})
async function clearAll() {
  if (!canClear.value || busy.value) return
  const target = scope()
  if (!confirm(`Clear every poll for ${collegeLabel(selected.value, colleges.value)}? Students can vote again.`)) return
  busy.value = true
  error.value = ''
  status.value = ''
  try {
    setAdminSession(target)
    let deleted = 0
    for (const slide of slides.filter(s => s.poll)) {
      const res = await clearPollResults(slide.storageId, target)
      deleted += res.deleted || 0
    }
    window.dispatchEvent(new CustomEvent('u80-poll-cleared'))
    status.value = deleted > 0
      ? `Cleared ${deleted} vote record${deleted === 1 ? '' : 's'} for ${target.collegeName}.`
      : `No votes to clear for ${target.collegeName}.`
  } catch (e) {
    error.value = e.message || 'Could not clear votes'
  } finally { busy.value = false }
}
onMounted(async () => {
  try {
    const res = await adminListColleges()
    colleges.value = res.colleges || []
    const viewing = getAdminSession()
    const college = colleges.value.find(c => c.id === viewing.collegeId) ||
      colleges.value.find(c => c.name.toLowerCase() === viewing.collegeName?.toLowerCase())
    collegeId.value = college?.id || ''
  } catch (e) { error.value = e.message || 'Could not load colleges' }
})
</script>

<style scoped>
.admin h1 {
  margin-top: 0;
}

.actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.actions a {
  text-decoration: none;
}

.polls {
  margin-top: 2rem;
}

.polls h2 {
  margin: 0 0 0.4rem;
  font-size: 1.15rem;
}

.scope {
  display: grid;
  gap: 0.75rem;
  margin: 1rem 0;
}

@media (min-width: 640px) {
  .scope {
    grid-template-columns: 1fr 1fr;
  }
}

.scope label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.scope select {
  font: inherit;
  font-weight: 400;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--cream);
}

.error {
  color: var(--danger);
  margin: 0 0 0.75rem;
}

.status {
  margin: 0 0 0.75rem;
  font-weight: 600;
}

</style>
